import sortBy from 'lodash/sortBy';

import {ToastType} from 'common/components/toast/types';
import {Store} from 'common/report/stores/store';
import {SectionItem} from 'common/components/items-menu/types';
import {assert, baseComparator} from 'common/util';
import {ComponentLifetimeStartEventName} from 'common/util/component-helpers';
import {LoadingType} from 'common/util/workflow-timing';
import {OrderBy} from 'common/util/array';
import {propertyDefinitionsClient} from 'common/report/queries/app-api/property-definitions-client';
import {eventDefinitionsClient} from 'common/report/queries/app-api/event-definitions-client';
import {ApiResponseError} from 'common/util/shared-client';
import {cohortsClient} from 'common/report/queries/app-api/cohorts-client';
import {Toast} from 'common/components/toast-area';
import {MENU_HEIGHT_REDUCTION, SHORT_WINDOW_HEIGHT} from 'common/constants';

import * as PropertiesMenu from '../properties-menu/types';
import {CohortSegment} from '../segment-menu/types';
import {CHILD_MENU_HEIGHT, EVENTS_MENU_BODY_HEIGHT, TRACKING_EVENTS} from '../constants';

/**
 * Builds cohort definitions from a list of cohorts
 */
export function getCohortsDefinitions(cohorts) {
  return sortBy(
    (cohorts || []).map((cohort) => ({
      label: cohort.name,
      value: cohort.id,
      name: cohort.name,
      count: cohort.count,
      description: cohort.description,
      createdBy: cohort.created_by,
      lastEdited: cohort.last_edited,
      can_view: cohort.can_view,
      can_update_basic: cohort.can_update_basic,
    })),
    `label`,
  );
}

export function getCohortFromPropertyItem(item: PropertiesMenu.Item): CohortSegment {
  assert(PropertiesMenu.isCohort(item));

  let cohort;
  if (PropertiesMenu.isInlineCohort(item)) {
    cohort = item.value.cohort;
    cohort = {raw_cohort: {name: item.value.cohort.name || item.label, ...cohort.raw_cohort}, negated: false};
  } else {
    cohort = {id: item.value};
    cohort = {...(item.dataGroupId ? {data_group_id: item.dataGroupId} : {}), ...cohort};
  }

  return {cohort: {...cohort, name: item.label, negated: false}};
}

/**
 * Updates a given Entity type description
 * @param entityStore
 * @param newDescription
 * @param storeKey
 * @param updateRequestObj
 * @param undoRequestObj
 * @param entityType
 */
export async function updateEntityDescription({
  entityStore,
  newDescription,
  storeKey,
  updateRequestObj,
  undoRequestObj,
  entityType,
  addToast,
  track,
}: {
  entityStore: Store<any>;
  newDescription: string;
  storeKey: string;
  updateRequestObj: Record<string, any>;
  undoRequestObj: Record<string, any>;
  entityType: string;
  addToast: (toast: Toast) => void;
  track: (...args) => void;
}) {
  const descriptionBeforeUpdate = entityStore.value[storeKey]?.description ?? null;

  const updateStoreWithDescription = (description: string) =>
    entityStore.update({
      [storeKey]: {
        ...entityStore.value[storeKey],
        description,
      },
    });

  const undoCallback = async () => {
    try {
      updateStoreWithDescription(descriptionBeforeUpdate);
      await entityStore.patch({
        ...undoRequestObj,
      });
    } catch (err) {
      addToast({message: `Unable to undo the description`, isError: true, type: ToastType.ERROR});
      updateStoreWithDescription(newDescription);
    }
  };

  track(
    TRACKING_EVENTS.EDIT_DESCRIPTION,
    {
      'New description': newDescription,
      'Old description': descriptionBeforeUpdate,
      'Access point': `context panel`,
      'Entity type': entityType,
    },
    false,
  );

  updateStoreWithDescription(newDescription);
  const storeValue = entityStore.value[storeKey];
  try {
    const response = await patchEntityDescription(updateRequestObj);
    // There is an issue with the some stores not updating values on patch. This means name is null when creating a new item.
    // fallback to the response value.
    addToast({
      message: `'${storeValue.name || response.name}' has been updated`,
      type: ToastType.SUCCESS,
      undoable: true,
      undoCallback,
    });
  } catch (err) {
    addToast({message: `Failed to update the description`, isError: true, type: ToastType.ERROR});
    updateStoreWithDescription(descriptionBeforeUpdate);
  }
}

export async function patchEntityDescription(params) {
  // Entities are either events, properties or cohorts
  let apiClient;
  if (params.propertyDefinition || params.eventDefinition) {
    apiClient = params.propertyDefinition ? propertyDefinitionsClient : eventDefinitionsClient;
  } else {
    apiClient = cohortsClient;
  }

  const result = await apiClient.query(params, {method: `PATCH`});
  if (result.status === `error`) {
    throw new ApiResponseError(result, result.error);
  }

  return result;
}

export function sortItemsByLabel<T extends SectionItem>(
  items: T[],
  {order}: {order: OrderBy} = {order: OrderBy.Ascending},
) {
  // todo: i18n
  return items.sort(baseComparator({order, transform: (item: T) => item.label.toString().toLowerCase()}));
}

export enum OpenedMenuComponent {
  ItemsMenu = `items_menu`,
  LoadingSkeleton = `loading_skeleton`,
}
export function getOpenedMenuComponent(ev: CustomEvent, component: HTMLElement): OpenedMenuComponent {
  function isSkeletonRenderedEvent(ev: CustomEvent, component: HTMLElement): boolean {
    return (
      ev.detail.eventName.includes(ComponentLifetimeStartEventName.LoadingAdded) &&
      ev.detail.props.type === LoadingType.Skeleton &&
      ev.detail.props.parentPath.includes(component.tagName.toLowerCase())
    );
  }

  function isItemsMenuRenderedEvent(ev: CustomEvent, component: HTMLElement): boolean {
    return (
      ev.detail.eventName.includes(ComponentLifetimeStartEventName.ComponentAdded) &&
      ev.detail.props.sourceComponent === `mp-items-menu` &&
      ev.detail.props.parentPath.includes(component.tagName.toLowerCase())
    );
  }

  if (isItemsMenuRenderedEvent(ev, component)) {
    return OpenedMenuComponent.ItemsMenu;
  } else if (isSkeletonRenderedEvent(ev, component)) {
    return OpenedMenuComponent.LoadingSkeleton;
  }
  return null;
}

export function getChildMenuHeight(): number {
  return window.innerHeight <= SHORT_WINDOW_HEIGHT ? CHILD_MENU_HEIGHT - MENU_HEIGHT_REDUCTION : CHILD_MENU_HEIGHT;
}

export function getEventsMenuBodyHeight(): number {
  return window.innerHeight <= SHORT_WINDOW_HEIGHT
    ? EVENTS_MENU_BODY_HEIGHT - MENU_HEIGHT_REDUCTION
    : EVENTS_MENU_BODY_HEIGHT;
}
